import React, { useRef, useState } from 'react';
import { HStack, Button, Text, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { AttachmentIcon, ViewIcon, DeleteIcon } from '@chakra-ui/icons';
import { uploadApi, API_BASE_URL } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';

interface EvidenceUploadProps {
  value: string;
  fileName?: string | null;
  fiscalYearId?: number;
  onChange: (link: string, name: string) => void;
}

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx';

const extractFileId = (url: string): number | null => {
  const prefix = '/api/uploads/evidence/';
  if (url.startsWith(prefix)) {
    const id = Number(url.slice(prefix.length));
    if (Number.isInteger(id) && id > 0) return id;
  }
  return null;
};

const EvidenceUpload: React.FC<EvidenceUploadProps> = ({ value, fileName, fiscalYearId, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  const removeOldFile = async () => {
    const id = extractFileId(value);
    if (id) {
      try { await uploadApi.deleteEvidence(id); } catch (_) { /* ignore */ }
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.uploadEvidence(file, fiscalYearId);
      await removeOldFile();
      onChange(res.data.url, res.data.original_name);
      toast({ title: t('upload.uploaded'), status: 'success', duration: 2000 });
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('upload.failed'), status: 'error' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clear = () => {
    removeOldFile();
    onChange('', '');
  };

  return (
    <HStack spacing={2} alignItems="center">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={handleUpload}
      />
      <Button
        size="sm"
        leftIcon={<AttachmentIcon />}
        colorScheme="green"
        variant="outline"
        isLoading={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {value ? t('upload.replaceFile') : t('upload.uploadEvidence')}
      </Button>
      {value && (
        <>
          <Text fontSize="xs" color="gray.600" noOfLines={1} maxW="200px">
            {fileName || t('upload.evidence')}
          </Text>
          <Tooltip label={t('upload.view')}>
            <IconButton
              aria-label="View file"
              icon={<ViewIcon />}
              size="xs"
              colorScheme="blue"
              variant="ghost"
              as="a"
              href={`${API_BASE_URL}${value}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          </Tooltip>
          <Tooltip label={t('upload.remove')}>
            <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={clear} />
          </Tooltip>
        </>
      )}
    </HStack>
  );
};

export default EvidenceUpload;
